import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import IntegrationDetailCard from '~/components/Admin/IntegrationPage/IntegrationDetailCard';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { clearVendorConnecting } from '~/redux/slices/eSignature';

import {
  ESignatureIntegration,
  ESignatureIntegrationCardProps,
  EsignatureIntegrationType,
  MODAL_KEYS,
} from '../types';
import {
  getIntegrationDetails,
  getIntegrationStatusType,
} from '../utils/helper';
import { CardFooterActions } from './CardFooterActions';
import { ESignatureAuthenticationOptions } from './ESignatureAuthenticationOptions';
import { useESignatureIntegrationActions } from './hook';
import { useIntegrationState } from './hooks/useIntegrationState';
import { IntegrationStatusMessage } from './IntegrationStatusMessage';
import { IntegrationsModal } from './modals/IntegrationsModal';
import { useIntegrationsModalController } from './modals/useIntegrationsModalController';
import { StatusAndActions } from './StatusAndActions';
import { UserManagementSection } from './UserManagementSection';

export const ESignatureIntegrationCard = ({
  esignatureIntegration,
  activeEsignatureIntegrationCount = 0,
  onRefetch = () => {},
}: ESignatureIntegrationCardProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [integrationDraft, setIntegrationDraft] = useState(
    esignatureIntegration,
  );

  useEffect(() => {
    setIntegrationDraft(esignatureIntegration);
  }, [esignatureIntegration]);
  useEffect(() => {
    if (
      esignatureIntegration?.isActive &&
      esignatureIntegration?.integrationType === 'company' &&
      esignatureIntegration?.providerName
    ) {
      dispatch(clearVendorConnecting());
    }
  }, [
    dispatch,
    esignatureIntegration?.isActive,
    esignatureIntegration?.providerName,
    esignatureIntegration?.integrationType,
  ]);

  const integrationDetails = getIntegrationDetails(
    esignatureIntegration?.providerName,
  );

  const {
    isLoading,
    executeCreateIntegration,
    executeReauthenticate,
    isCompanyConnecting,
  } = useESignatureIntegrationActions({
    draft: integrationDraft,
  });

  const integrationState = useIntegrationState({
    integrationDraft,
    esignatureIntegration,
    activeEsignatureIntegrationCount,
  });

  const { isUnchanged, isActive, isRealIntegration, hasError, isDisabled } =
    integrationState;

  const statusType = getIntegrationStatusType(isActive, isDisabled, hasError);

  const { closeModal, setModalState } =
    useIntegrationsModalController(onRefetch);

  const handleCancel = () => {
    setIsCollapsed(true);
    closeModal();
    setIntegrationDraft(esignatureIntegration);
  };

  const handleConnect = async () => {
    executeCreateIntegration();
  };

  const handleUpdate = () => {
    if (integrationDraft?.id && integrationDraft?.integrationType) {
      setModalState({
        name: MODAL_KEYS.UPDATE,
        integrationId: integrationDraft.id,
        providerName: integrationDetails?.name ?? '',
        integrationType: integrationDraft.integrationType,
      });
    }
  };

  const handleReconnect = () => {
    executeReauthenticate();
  };

  const footer = (
    <CardFooterActions
      isConnected={isRealIntegration}
      isUnchanged={isUnchanged}
      isLoading={isLoading}
      hasError={hasError}
      hasActiveIntegration={activeEsignatureIntegrationCount > 0}
      disabled={isDisabled}
      onCancel={handleCancel}
      onConnect={handleConnect}
      onReconnect={handleReconnect}
      onUpdate={handleUpdate}
    />
  );

  const handleAuthOptionChange = (value?: EsignatureIntegrationType) => {
    if (!integrationDraft || !value) return;

    const updatedDraft: ESignatureIntegration = {
      ...integrationDraft,
      integrationType: value,
    };

    setIntegrationDraft(updatedDraft);
  };

  const handleUserManagementClick = () => {
    integrationDraft?.id &&
      setModalState({
        name: MODAL_KEYS.MANAGE,
        integrationId: integrationDraft?.id,
        integrationType: integrationDraft?.integrationType,
        providerName: integrationDetails?.name ?? '',
      });
  };

  const handleDeleteButtonClick = () => {
    setIsCollapsed(false);
    setModalState({
      name: MODAL_KEYS.DELETE,
      integrationId: integrationDraft?.id ?? '',
      providerName: integrationDetails?.name ?? '',
    });
  };

  return (
    <IntegrationDetailCard
      isCollapsed={isCollapsed}
      footer={footer}
      headerRightContent={
        <StatusAndActions
          disabled={isDisabled}
          integration={esignatureIntegration}
          isConnected={isRealIntegration}
          isActive={!isCollapsed}
          isLoading={isLoading}
          isCompanyConnecting={isCompanyConnecting}
          onConnect={() => setIsCollapsed(false)}
          onDelete={handleDeleteButtonClick}
        />
      }
      logoSrc={integrationDetails?.logoSrc}
      title={integrationDetails?.name}
      onToggleCollapse={setIsCollapsed}
    >
      <IntegrationsModal />
      <Layout direction="column" spacing={8}>
        {statusType && (
          <IntegrationStatusMessage
            errorMessage={esignatureIntegration?.error?.message}
            providerName={integrationDetails?.name ?? ''}
            type={statusType}
          />
        )}
        <Text variant="body" disabled={isDisabled}>
          {intl.formatMessage(
            {
              id: 'NewESignature.ESignatureIntegrationCard.DetailsAboutUsingIntegration',
              defaultMessage: `Use {providerName}'s e-signature tool for your workflows. Contracts created or processed through our workflow tool will be routed for signature using {providerName}.`,
              description:
                'Header messages about using the e-signature integration',
            },
            { providerName: integrationDetails?.name },
          )}
        </Text>
        <ESignatureAuthenticationOptions
          disabled={isDisabled}
          providerName={integrationDetails?.name}
          integration={integrationDraft}
          onChange={handleAuthOptionChange}
        />
        <UserManagementSection
          disabled={isDisabled}
          isChangeDetected={isLoading || !isUnchanged}
          authenticatedUserCount={
            esignatureIntegration?.authenticatedUserCount ?? 0
          }
          onClick={handleUserManagementClick}
        />
      </Layout>
    </IntegrationDetailCard>
  );
};
